import { useRef, useState } from 'react';
import { Modal, InputNumber, Alert, Space, Typography, Button } from '../ui';

export type Coords = { lat: number; lng: number };

// Default seed for the manual picker — a real coordinate (Pune) so a desktop/server
// tester can confirm in one click and adjust as needed.
const DEFAULT_LAT = 18.5204;
const DEFAULT_LNG = 73.8567;

// Try the device GPS (network/Wi-Fi based; works on phones and most laptops).
function getDevicePosition(): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('no-geolocation'));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (e) => reject(e),
      // enableHighAccuracy:false uses network/Wi-Fi location; `true` forces GPS
      // hardware many desktops lack → it times out. maximumAge reuses a recent fix.
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 },
    );
  });
}

/**
 * Resolve a location for GPS-gated actions (attendance punch, visit check-in).
 *
 * Tries the device's location first. If it's unavailable — desktops/servers with
 * no GPS and no Wi-Fi positioning return POSITION_UNAVAILABLE even when the browser
 * permission is granted — it falls back to a manual lat/lng entry modal so the
 * workflow still works. The mobile app, which has real GPS, never sees the modal.
 *
 * Usage:
 *   const geo = useGeoOrManual();
 *   const pos = await geo.resolve();   // null if the user cancels the manual entry
 *   ...render {geo.modal} once in the tree...
 */
export function useGeoOrManual() {
  const [open, setOpen] = useState(false);
  const [denied, setDenied] = useState(false);
  const [lat, setLat] = useState<number | null>(DEFAULT_LAT);
  const [lng, setLng] = useState<number | null>(DEFAULT_LNG);
  const resolver = useRef<((c: Coords | null) => void) | null>(null);
  const lastUsed = useRef<Coords | null>(null);

  const resolve = (): Promise<Coords | null> =>
    getDevicePosition().then(
      (pos) => {
        lastUsed.current = pos;
        return pos as Coords | null;
      },
      (e: any) =>
        new Promise<Coords | null>((res) => {
          setDenied(e?.code === 1); // 1 = PERMISSION_DENIED
          const seed = lastUsed.current;
          setLat(seed?.lat ?? DEFAULT_LAT);
          setLng(seed?.lng ?? DEFAULT_LNG);
          resolver.current = res;
          setOpen(true);
        }),
    );

  const finish = (c: Coords | null) => {
    setOpen(false);
    const r = resolver.current;
    resolver.current = null;
    r?.(c);
  };

  const modal = (
    <Modal
      open={open}
      title="Enter location manually"
      onOpenChange={(o) => { if (!o) finish(null); }}
      footer={
        <>
          <Button variant="default" onClick={() => finish(null)}>Cancel</Button>
          <Button
            variant="primary"
            disabled={lat == null || lng == null}
            onClick={() => {
              const c = { lat: Number(lat), lng: Number(lng) };
              lastUsed.current = c;
              finish(c);
            }}
          >
            Use this location
          </Button>
        </>
      }
    >
      <Alert
        type={denied ? 'warning' : 'info'}
        showIcon
        style={{ marginBottom: 12 }}
        message={denied ? 'Location permission is blocked' : "Couldn't get your location automatically"}
        description={
          denied
            ? 'Allow location for this site (the icon in the address bar) for automatic capture, or enter coordinates below to continue.'
            : 'This device has no GPS / Wi-Fi positioning (common on desktops & servers), so the browser can’t resolve a location. Enter coordinates to continue — the mobile app captures GPS automatically.'
        }
      />
      <Space>
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Latitude</div>
          <InputNumber value={lat as any} onChange={(v) => setLat(v as number)} style={{ width: 180 }} placeholder="e.g. 18.5204" step={0.0001} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Longitude</div>
          <InputNumber value={lng as any} onChange={(v) => setLng(v as number)} style={{ width: 180 }} placeholder="e.g. 73.8567" step={0.0001} />
        </div>
      </Space>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
        Tip: in Google Maps, right-click a spot → the first menu item is &ldquo;lat, lng&rdquo;. Paste the two numbers here.
      </Typography.Paragraph>
    </Modal>
  );

  return { resolve, modal };
}
